/**
 * Ghost cards for what Partnr is putting on the canvas, drawn while the tool
 * input still streams (contract section 3). Mounted inside InfiniteCanvas's
 * transformed layer, so ghosts pan and zoom with the canvas.
 *
 * - Reads partnrPanelStore.liveMessage. For each create_canvas_items,
 *   create_diagram, add_links, add_images and clip_to_board part in
 *   input-streaming or input-available, it reserves one open block per
 *   toolCallId (inside the visible area, left of the open panel) and grows it
 *   as items arrive, without moving earlier ghosts. A block that could only
 *   start off screen pans into view at its first ghost (ghostCamera.ts).
 * - The executor takes the block's positions when the action applies and
 *   lands it: ghosts leave in order over the real cards.
 * - output-error, output-denied or an output with no canvas action: ghosts fade.
 *
 * Everything that writes (reserving, growing, fading) runs in effects, never
 * during render; there is no ResizeObserver.
 */
import React, { useEffect, useRef } from 'react';
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion';
import { cn } from '@/lib/utils';
import { usePartnrPanelStore } from '@/stores/partnrPanelStore';
import { useCanvasStore } from '@/stores/canvasStore';
import { GHOST_FADE_MS, LAND_STAGGER_MS, usePartnrGhostStore, type GhostBlock, type GhostSlot } from '@/lib/partnr/ghosts';
import { syncGhostsFromMessage } from '@/lib/partnr/ghostSync';
import { watchCanvasInteraction } from '@/lib/canvasInteraction';

const SPRING = { type: 'spring' as const, stiffness: 500, damping: 38 };
const EASE_OUT = [0.22, 1, 0.36, 1] as const;
/** A block nobody touched for this long (a stream that died quietly) fades. */
const STALE_MS = 30_000;

/** Words fade and unblur in as they stream; the ones already shown stay put. */
function StreamedText({ text, className, still }: { text: string; className?: string; still: boolean }) {
  const words = text.split(/(\s+)/).filter(Boolean);
  return (
    <span className={className}>
      {words.map((word, i) =>
        still || /^\s+$/.test(word) ? (
          <React.Fragment key={i}>{word}</React.Fragment>
        ) : (
          <motion.span
            key={i}
            initial={{ opacity: 0, filter: 'blur(4px)' }}
            animate={{ opacity: 1, filter: 'blur(0px)' }}
            transition={{ duration: 0.24, ease: EASE_OUT }}
          >
            {word}
          </motion.span>
        ),
      )}
    </span>
  );
}

function hostOf(title: string): string {
  return title.replace(/^https?:\/\//, '').split('/')[0];
}

function GhostCard({ slot, index, phase, still }: { slot: GhostSlot; index: number; phase: GhostBlock['phase']; still: boolean }) {
  const leaving = phase === 'landing' || phase === 'fading';
  const delay = phase === 'landing' ? (index * LAND_STAGGER_MS) / 1000 : 0;
  return (
    <motion.div
      data-partnr-ghost
      className="absolute overflow-hidden rounded-[14px] border border-line bg-paper-raised text-left shadow-sm"
      style={{ width: slot.width, height: slot.height }}
      initial={still ? { opacity: 0, x: slot.x, y: slot.y } : { opacity: 0, scale: 0.96, x: slot.x, y: slot.y }}
      animate={{
        opacity: leaving ? 0 : 1,
        scale: still ? 1 : phase === 'fading' ? 0.97 : 1,
        x: slot.x,
        y: slot.y,
      }}
      transition={{
        opacity: { duration: still ? 0 : GHOST_FADE_MS / 1000, delay: still ? 0 : delay, ease: EASE_OUT },
        scale: { duration: still ? 0 : 0.2, ease: EASE_OUT },
        x: still ? { duration: 0 } : SPRING,
        y: still ? { duration: 0 } : SPRING,
      }}
    >
      {/* Slow breathing wash while the item is still being written */}
      {!slot.complete && !still && (
        <motion.div
          aria-hidden
          className="absolute inset-0 bg-wash"
          initial={{ opacity: 0.2 }}
          animate={{ opacity: [0.2, 0.7, 0.2] }}
          transition={{ duration: 2.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      )}
      <div className="relative flex h-full flex-col gap-1.5 p-3">
        {slot.kind === 'image' ? (
          <>
            <div className="min-h-0 flex-1 rounded-[10px] bg-paper-sunken" />
            {slot.title && <StreamedText text={slot.title} still={still} className="line-clamp-1 text-[12px] leading-4 text-ink-3" />}
          </>
        ) : slot.kind === 'quote' ? (
          <>
            <div className="border-l-2 border-line pl-2.5">
              <StreamedText text={slot.body} still={still} className="line-clamp-[12] text-[13px] italic leading-5 text-ink-2" />
            </div>
            {slot.title && <StreamedText text={slot.title} still={still} className="mt-auto line-clamp-1 text-[11px] leading-4 text-ink-3" />}
          </>
        ) : slot.kind === 'link' ? (
          <>
            {slot.height > 200 && <div className="min-h-0 flex-1 rounded-[10px] bg-paper-sunken" />}
            <StreamedText text={slot.title} still={still} className="line-clamp-2 text-[13px] font-medium leading-5 text-ink" />
            {slot.body ? (
              <StreamedText text={slot.body} still={still} className="line-clamp-2 text-[12px] leading-4 text-ink-3" />
            ) : (
              <span className="font-mono text-[11px] leading-4 text-ink-3">{hostOf(slot.title)}</span>
            )}
          </>
        ) : (
          <>
            {slot.title && (
              <StreamedText text={slot.title} still={still} className={cn('line-clamp-2 text-[14px] leading-[22px] text-ink', slot.itemType !== 'sticky' && 'font-medium')} />
            )}
            {slot.body && (
              // A sticky keeps its title as the first line, then a blank line: the ghost matches the card it becomes
              <StreamedText
                text={slot.body}
                still={still}
                className={cn('line-clamp-6', slot.itemType === 'sticky' ? 'mt-[16px] text-[14px] leading-[22px] text-ink' : 'text-[13px] leading-5 text-ink-2')}
              />
            )}
          </>
        )}
      </div>
    </motion.div>
  );
}

function GhostEdges({ block, still }: { block: GhostBlock; still: boolean }) {
  if (block.edges.length === 0) return null;
  const leaving = block.phase === 'landing' || block.phase === 'fading';
  const center = (s: GhostSlot) => ({ x: s.x + s.width / 2, y: s.y + s.height / 2 });
  return (
    <svg className="pointer-events-none absolute left-0 top-0 overflow-visible" width={1} height={1} aria-hidden>
      {block.edges.map(([a, b]) => {
        const from = block.slots[a];
        const to = block.slots[b];
        if (!from || !to) return null;
        const p = center(from);
        const q = center(to);
        return (
          <motion.line
            key={`${a}-${b}`}
            x1={p.x}
            y1={p.y}
            x2={q.x}
            y2={q.y}
            className="stroke-ink-3"
            strokeWidth={1.5}
            strokeDasharray="4 4"
            initial={{ opacity: 0 }}
            animate={{ opacity: leaving ? 0 : 0.6 }}
            transition={{ duration: still ? 0 : 0.24, ease: EASE_OUT }}
          />
        );
      })}
    </svg>
  );
}

export function PartnrGhostLayer({ className }: { className?: string }) {
  const liveMessage = usePartnrPanelStore((s) => s.liveMessage);
  const currentBoardId = useCanvasStore((s) => s.currentBoardId);
  const blocks = usePartnrGhostStore((s) => s.blocks);
  const still = !!useReducedMotion();
  // Diagrams already moved to their final layout; only touched in effects
  const laidOut = useRef(new Set<string>());

  // So the pan to an off-screen block never fights the user's own drag or scroll
  useEffect(() => watchCanvasInteraction(), []);

  useEffect(() => {
    syncGhostsFromMessage(liveMessage, currentBoardId, laidOut.current);
  }, [liveMessage, currentBoardId]);

  // Safety net: a block nobody updated or took for a while fades
  const hasBlocks = Object.keys(blocks).length > 0;
  useEffect(() => {
    if (!hasBlocks) return;
    const timer = setInterval(() => {
      const now = Date.now();
      for (const block of Object.values(usePartnrGhostStore.getState().blocks)) {
        if (!block.taken && (block.phase === 'streaming' || block.phase === 'ready') && now - block.updatedAt > STALE_MS) {
          usePartnrGhostStore.getState().fade(block.key);
        }
      }
    }, 5000);
    return () => clearInterval(timer);
  }, [hasBlocks]);

  const shown = Object.values(blocks).filter((b) => b.boardId === currentBoardId);
  return (
    <div
      data-export-ignore="true"
      data-partnr-ghost-layer
      aria-hidden
      className={cn('pointer-events-none absolute left-0 top-0', className)}
      // Above every card: landing ghosts fade out over the real ones
      style={{ zIndex: 2_000_000 }}
    >
      <AnimatePresence>
        {shown.map((block) => (
          <motion.div key={block.key} exit={{ opacity: 0 }} transition={{ duration: still ? 0 : 0.15 }}>
            <GhostEdges block={block} still={still} />
            {block.slots.map((slot, i) => (
              <GhostCard key={i} slot={slot} index={i} phase={block.phase} still={still} />
            ))}
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
}
